import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";

import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";

import { Delete, Edit } from "@mui/icons-material";

import { editProjectTrigger, deleteProject } from "actions/projectActions";

import { ImageEditor } from "components";
import { DEFAULT_IMAGES, IMAGE_TYPE } from "config";

const locationIcon = "/assets/icons/location_icon.png";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const missingImageAlt = DEFAULT_IMAGES.PROJECT;

function FlipCard({
  supplierId,
  projectName,
  clientName,
  location,
  description,
  imageUrl,
  isPublicProfile,
  projectData,
  permissions,
  projectId,
  ...rest
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleEditProject = () => {
    // console.log("handleEditProject", projectName);
    dispatch(editProjectTrigger(projectData));
    navigate("/add-project");
  };

  const handleDeleteProject = () => {
    // console.log("handleDeleteProject", projectName);
    dispatch(deleteProject(supplierId, projectName, projectId));
  };

  return (
    <Grid
      sx={{
        position: "relative",
        width: "315px",
        height: "375px",
        backgroundColor: "transparent",
        borderRadius: "20px",
      }}
    >
      <Grid>
        {permissions && permissions.isUpdateAllowed && (
          <Box
            mr={6}
            sx={{ display: "flex", justifyContent: "end", textAlign: "right" }}
          >
            <>
              <Typography variant="body2" sx={{ mr: 0 }}>
                <Tooltip title="Edit">
                  <IconButton
                  disabled={!permissions?.isOwner}
                    sx={{ color: "primary.main" }}
                    onClick={handleEditProject}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Typography>

              <Typography variant="body2">
                <Tooltip title="Delete">
                  <IconButton
                  disabled={!permissions?.isOwner}
                    sx={{ color: "primary.main" }}
                    onClick={handleDeleteProject}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Typography>
            </>
          </Box>
        )}
        <Grid
          xs={12}
          sx={{
            position: "absolute",
            width: "315px",
            height: "375px",
            transformStyle: "preserve-3d",
            transition: "all 0.7s ease, border 0.3s",
            "&:hover": {
              borderColor: "transparent",
              transform: "rotateY(180deg)",
              borderRadius: "20px",
            },
          }}
        >
          {imageUrl ? (
            <Card
              variant="outlined"
              sx={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                position: "absolute",
                backfaceVisibility: "hidden",
                borderRadius: "20px",
              }}
            >
              <CardMedia
                  component="img"
                  sx={{ height: "375px", width: "315px" }}
                  image={imageUrl}
                  alt={projectName}
                />
            </Card>
          ) : (
            <Card
              variant="outlined"
              sx={{
                width: "100%",
                height: "100%",
                overflow: "hidden",
                position: "absolute",
                backfaceVisibility: "hidden",
                borderRadius: "20px",
                justifyContent: "center",
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                alignItems: "center",
              }}
              style={{
                backgroundImage: `url(${missingImageAlt})`,
                height: "375px",
                width: "315px",
              }}
            >
              <CardContent
                sx={{
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="subtitle1"
                  textTransform="uppercase"
                  textAlign="center"
                  fontWeight={"700"}
                  color={"#ffffff"}
                  mb={1}
                >
                  {projectName}
                </Typography>
              </CardContent>
            </Card>
          )}

          <Card
            variant="outlined"
            sx={{
              width: "100%",
              height: "100%",
              overflow: "hidden",
              position: "absolute",
              transform: "rotateY(180deg)",
              backfaceVisibility: "hidden",
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              //   border:"solid",
              //   borderColor:"#3963AB"
            }}
          >
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    textTransform="uppercase"
                    // fontSize={"14px"}
                    // fontWeight={"bold"}
                    sx={{ color: "rgba(0, 0, 0, 0.8)" }}
                  >
                    {projectName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    textTransform="capitalize"
                    // fontSize={"12px"}
                    // fontWeight={"600"}
                    sx={{ color: "rgba(0, 0, 0, 0.8)" }}
                  >
                    {clientName}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={12}
                  display="flex"
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <Grid>
                    <img src={locationIcon} />
                  </Grid>
                  <Grid>
                    <Typography
                      variant="body1"
                      // fontSize={"12px"}
                      // fontWeight={"500"}
                      sx={{ color: "rgba(0, 0, 0, 0.8)" }}
                    >
                      {location}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    // fontSize={"12px"}
                    // fontWeight={"500"}
                    sx={{ color: "rgba(0, 0, 0, 0.8)" }}
                  >
                    {description}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {/* ))} */}
      </Grid>
      {permissions && !permissions.isViewOnly && permissions.isUpdateAllowed && (
        <Box
          style={{
            mt: 3,
            ml: 2
          }}
        >
          <ImageEditor
            type={IMAGE_TYPE.PROJECT}
            name={projectName}
            supplierId={supplierId}
            inImage={
              imageUrl ? `${BASE_URL}/${imageUrl}` : DEFAULT_IMAGES.PROJECT
            }
            id={projectId}
          />
        </Box>
      )}
    </Grid>
  );
}

FlipCard.propTypes = {
  projectName: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  location: PropTypes.string,
  description: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  isPublicProfile: PropTypes.bool,
  serviceData: PropTypes.object,
  supplierId: PropTypes.number,
  permissions: PropTypes.object,
  projectId: PropTypes.string,
};

export default FlipCard;
